import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  FACEBOOK_SDK_DEFAULT_LOCALE,
  FACEBOOK_SDK_DEFAULT_VERSION,
  META_EMBEDDED_SIGNUP_LOGIN_EXTRAS,
} from "../constants/facebookSdk";
import {
  getFacebookSdk,
  isTrustedFacebookOrigin,
  loadFacebookSdk,
  type FacebookLoginResponse,
} from "../utils/facebookSdk";
import {
  extractEmbeddedSignupErrorMessage,
  extractWabaFromEmbeddedSignupMessage,
  getIsSecureContextForFbLogin,
  isEmbeddedSignupFinishEvent,
  isEmbeddedSignupTerminalErrorEvent,
  isSameMetaPayload,
  mergeMetaConnectionResult,
  META_EMBEDDED_SIGNUP_LOGIN_TIMEOUT_MS,
  metaConnectionSignature,
  normalizeEmbeddedSignupMessage,
  resolveEmbeddedSignupConfigurationError,
} from "../utils/metaEmbeddedSignupUtils";
import type { CrmSetupMetaConnectionResult } from "../types";

export interface UseCrmSetupMetaEmbeddedSignupResult {
  isReady: boolean;
  isLoading: boolean;
  isAwaitingWaba: boolean;
  error: string | null;
  result: CrmSetupMetaConnectionResult | null;
  hasConfiguration: boolean;
  isSecureContext: boolean;
  openEmbeddedSignup: () => void;
  resetResult: () => void;
}

/**
 * Embedded Signup Meta. Opcionalmente notifica quando o fluxo retorna WABA (FINISH), para gravar no
 * estado fora de useEffect — evita loops por dependências em `result`.
 */
export function useCrmSetupMetaEmbeddedSignup(
  onWabaEmbeddedFinish?: (
    result: CrmSetupMetaConnectionResult,
  ) => void | Promise<void>,
): UseCrmSetupMetaEmbeddedSignupResult {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAwaitingWaba, setIsAwaitingWaba] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrmSetupMetaConnectionResult | null>(
    null,
  );

  const onFinishRef = useRef(onWabaEmbeddedFinish);
  onFinishRef.current = onWabaEmbeddedFinish;

  const resultRef = useRef<CrmSetupMetaConnectionResult | null>(null);
  const lastNotifiedFinishSignatureRef = useRef<string | null>(null);
  const loginInFlightRef = useRef(false);
  const loadingWatchdogRef = useRef<number | null>(null);

  const appId = import.meta.env.VITE_META_APP_ID as string | undefined;
  const configId = import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID as
    | string
    | undefined;
  const hasConfiguration = Boolean(appId?.trim() && configId?.trim());
  const isSecureContext = useMemo(() => getIsSecureContextForFbLogin(), []);

  const clearSignupWatchdog = useCallback(() => {
    if (loadingWatchdogRef.current) {
      window.clearTimeout(loadingWatchdogRef.current);
      loadingWatchdogRef.current = null;
    }
  }, []);

  const stopSignupFlow = useCallback(
    (errorMessage?: string) => {
      clearSignupWatchdog();
      loginInFlightRef.current = false;
      setIsLoading(false);
      setIsAwaitingWaba(false);
      if (errorMessage) {
        setError(errorMessage);
      }
    },
    [clearSignupWatchdog],
  );

  const applyMetaConnectionResult = useCallback(
    (next: CrmSetupMetaConnectionResult) => {
      resultRef.current = next;
      setResult(next);
    },
    [],
  );

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedFacebookOrigin(event.origin)) return;

      const data = normalizeEmbeddedSignupMessage(event.data);
      if (!data || data.type !== "WA_EMBEDDED_SIGNUP") return;

      if (isEmbeddedSignupTerminalErrorEvent(data.event)) {
        const metaError = extractEmbeddedSignupErrorMessage(data);
        stopSignupFlow(
          data.event === "CANCEL"
            ? (metaError ?? "Vinculação cancelada na Meta.")
            : (metaError ??
                "A Meta retornou um erro durante a vinculação. Tente novamente."),
        );
        return;
      }

      if (!isEmbeddedSignupFinishEvent(data.event)) return;

      const { wabaId, wabaName, businessId } =
        extractWabaFromEmbeddedSignupMessage(data);

      const previousResult = resultRef.current;
      const next = mergeMetaConnectionResult(previousResult, {
        wabaId,
        businessId,
        wabaName,
      });

      if (isSameMetaPayload(previousResult, next)) {
        setError(null);
        stopSignupFlow();
        return;
      }

      if (next.wabaId && onFinishRef.current) {
        const sig = metaConnectionSignature(next);
        if (lastNotifiedFinishSignatureRef.current !== sig) {
          lastNotifiedFinishSignatureRef.current = sig;
          void Promise.resolve(onFinishRef.current(next)).catch(
            (finishError: unknown) => {
              lastNotifiedFinishSignatureRef.current = null;
              setError(
                finishError instanceof Error
                  ? finishError.message
                  : "Não foi possível concluir a vinculação Meta. Tente novamente.",
              );
            },
          );
        }
      }

      applyMetaConnectionResult(next);
      setError(null);
      stopSignupFlow();
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [applyMetaConnectionResult, stopSignupFlow]);

  useEffect(() => {
    const configurationError = resolveEmbeddedSignupConfigurationError({
      appId,
      configId,
    });

    if (configurationError) {
      setError(configurationError);
      setIsReady(false);
      return;
    }

    let isActive = true;
    setIsReady(false);
    void loadFacebookSdk({
      appId: appId!,
      locale: FACEBOOK_SDK_DEFAULT_LOCALE,
      version: FACEBOOK_SDK_DEFAULT_VERSION,
    })
      .then(() => {
        if (!isActive) return;
        setError(null);
        setIsReady(true);
      })
      .catch((sdkError: unknown) => {
        if (!isActive) return;
        setIsReady(false);
        setError(
          sdkError instanceof Error
            ? sdkError.message
            : "Não foi possível carregar o SDK da Meta/Facebook.",
        );
      });

    return () => {
      isActive = false;
    };
  }, [appId, configId]);

  const openEmbeddedSignup = useCallback(() => {
    const configurationError = resolveEmbeddedSignupConfigurationError({
      appId,
      configId,
    });

    if (configurationError) {
      setError(configurationError);
      return;
    }

    if (!isSecureContext) {
      setError("O login da Meta exige HTTPS. Use HTTPS ou localhost.");
      return;
    }

    const facebookSdk = getFacebookSdk();
    if (!isReady || !facebookSdk) {
      setError(
        "SDK da Meta/Facebook ainda está carregando. Aguarde alguns segundos e tente novamente.",
      );
      return;
    }

    if (loginInFlightRef.current) {
      return;
    }

    setError(null);
    lastNotifiedFinishSignatureRef.current = null;
    applyMetaConnectionResult(
      mergeMetaConnectionResult(null, {
        authorizationCode: null,
        wabaId: null,
        businessId: null,
        wabaName: null,
      }),
    );
    clearSignupWatchdog();

    loginInFlightRef.current = true;
    setIsLoading(true);
    setIsAwaitingWaba(false);

    loadingWatchdogRef.current = window.setTimeout(() => {
      loadingWatchdogRef.current = null;
      if (!loginInFlightRef.current) return;
      stopSignupFlow("A conexão com a Meta demorou demais. Tente novamente.");
    }, META_EMBEDDED_SIGNUP_LOGIN_TIMEOUT_MS);

    facebookSdk.login(
      (response: FacebookLoginResponse) => {
        if (response.status === "unknown" || !response.authResponse) {
          stopSignupFlow("Login cancelado ou falhou.");
          return;
        }

        if (response.authResponse.code) {
          const next = mergeMetaConnectionResult(resultRef.current, {
            authorizationCode: response.authResponse.code,
          });
          applyMetaConnectionResult(next);
          setIsAwaitingWaba(true);
          return;
        }

        stopSignupFlow("Login cancelado ou falhou.");
      },
      {
        config_id: configId!,
        response_type: "code",
        override_default_response_type: true,
        extras: META_EMBEDDED_SIGNUP_LOGIN_EXTRAS,
      },
    );
  }, [
    appId,
    applyMetaConnectionResult,
    clearSignupWatchdog,
    configId,
    isReady,
    isSecureContext,
    stopSignupFlow,
  ]);

  const resetResult = useCallback(() => {
    stopSignupFlow();
    lastNotifiedFinishSignatureRef.current = null;
    applyMetaConnectionResult(
      mergeMetaConnectionResult(null, {
        authorizationCode: null,
        wabaId: null,
        businessId: null,
        wabaName: null,
      }),
    );
    setError(null);
  }, [applyMetaConnectionResult, stopSignupFlow]);

  return {
    isReady,
    isLoading,
    isAwaitingWaba,
    error,
    result,
    hasConfiguration,
    isSecureContext,
    openEmbeddedSignup,
    resetResult,
  };
}
